/**
 * SizeGuide.jsx — EVORA Size Guide Page
 *
 * Fixes vs submitted version:
 *
 * 1. Free shipping on all orders (no threshold)
 * 2. <details> FAQ → controlled accordion (animatable, cross-browser safe)
 * 3. <li self-closing /> → valid HTML <li><span>›</span></li>
 * 4. aria-live="polite" added to table panel — screen readers announce
 *    gender switch (previously silent)
 * 5. FAQ item with JSX link uses data flag (hasLink:true) not idx===3
 * 6. useMemo on SIZE_DATA[gender] → direct assignment (no useMemo overhead
 *    for a constant object property lookup)
 * 7. og:image added to OG + Twitter blocks
 * 8. SITE_URL default: evoraclothing.com (matches rest of codebase)
 * 9. Page title improved for SEO: keyword-rich, transactional
 * 10. Gender tabs: width:100% flex on mobile (no clip on 320px)
 * 11. Inline size CTA added below size chart (captures peak intent)
 * 12. CTA band primary: "Shop" not "Shop Women"
 * 13. JSON-LD: FAQPage schema added for mini-FAQ section
 */

import { useState, useCallback, useId } from 'react';
import { Link }   from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Layout     from '../../components/common/Layout/Layout';
import { BRAND }  from '../../config/brand';
import './SizeGuide.css';

/* ── Constants ──────────────────────────────────────────────────────────── */
const SITE_URL = BRAND?.url || process.env.REACT_APP_SITE_URL || 'https://evoraclothing.com';
const BRAND_NAME = BRAND?.name || 'EVORA';
const SIZE_GUIDE_URL = `${SITE_URL}/size-guide`;
const OG_IMAGE = `${SITE_URL}/og-image.jpg`;

/* ── Static data — module scope, never recreated ────────────────────────── */
const SIZE_DATA = {
  men: [
    { size: 'XS',  chest: '34–36', shoulder: '16.5', length: '27' },
    { size: 'S',   chest: '36–38', shoulder: '17',   length: '28' },
    { size: 'M',   chest: '38–40', shoulder: '17.5', length: '29' },
    { size: 'L',   chest: '40–42', shoulder: '18',   length: '30' },
    { size: 'XL',  chest: '42–44', shoulder: '18.5', length: '31' },
    { size: 'XXL', chest: '44–46', shoulder: '19',   length: '32' },
  ],
  women: [
    { size: 'XS',  chest: '32–34', shoulder: '15.5', length: '25' },
    { size: 'S',   chest: '34–36', shoulder: '16',   length: '26' },
    { size: 'M',   chest: '36–38', shoulder: '16.5', length: '27' },
    { size: 'L',   chest: '38–40', shoulder: '17',   length: '28' },
    { size: 'XL',  chest: '40–42', shoulder: '17.5', length: '29' },
    { size: 'XXL', chest: '42–44', shoulder: '18',   length: '30' },
  ],
};

const MEASURE_STEPS = [
  {
    id: 'chest',
    title: 'Chest',
    text: 'Wrap the tape around the fullest part of your chest, keeping it horizontal under your arms. Snug, not tight.',
  },
  {
    id: 'shoulder',
    title: 'Shoulder',
    text: 'Measure across the back from the edge of one shoulder to the other. This determines garment width.',
  },
  {
    id: 'length',
    title: 'Length',
    text: 'From the top of the shoulder where the collar sits, straight down to the hem.',
  },
];

const FIT_CARDS = [
  {
    id: 'relaxed',
    title: 'Relaxed Fit',
    text: 'Our tees are designed for comfort — slightly relaxed, never restrictive. Prefer a fitted look? Size down one.',
  },
  {
    id: 'true-size',
    title: 'True to Size',
    text: 'Runs true to standard sizing. Your usual Medium from any premium brand will be a Medium here.',
  },
  {
    id: 'pre-shrunk',
    title: 'Pre-Shrunk Fabric',
    text: 'Bio-washed and pre-shrunk at manufacturing. The size you order is the size it stays, wash after wash.',
  },
];

// hasLink flag instead of idx === 3 — reorder-safe
const FAQ_ITEMS = [
  {
    id: 'faq-between',
    q: "What if I'm between sizes?",
    a: "Size up. Our relaxed fit means one size up gives a comfortable, slightly oversized look. Size down for a fitted look.",
  },
  {
    id: 'faq-runsize',
    q: 'Do your sizes run small or large?',
    a: 'True to standard sizing. The fit is relaxed rather than slim — not tight.',
  },
  {
    id: 'faq-shrink',
    q: 'Will the shirt shrink after washing?',
    a: 'No. Our fabrics are bio-washed and pre-shrunk during manufacturing. Your size is stable after multiple washes.',
  },
  {
    id: 'faq-wrongsize',
    q: "What if the size doesn't fit?",
    a: null,
    hasLink: true,
  },
  {
    id: 'faq-measure',
    q: 'How do I measure accurately?',
    a: 'Use a flexible measuring tape over light clothing or bare skin. Keep it horizontal and snug. Ask someone to help for shoulder width.',
  },
];

/* ── Structured data — serialized once at module level ──────────────────── */
const PAGE_SCHEMA = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type':       'WebPage',
      '@id':         `${SIZE_GUIDE_URL}#webpage`,
      url:           SIZE_GUIDE_URL,
      name:          `T-Shirt Size Guide for Men & Women | XS–XXL | ${BRAND_NAME}`,
      description:   `Complete size chart and measurement guide for ${BRAND_NAME} premium t-shirts. Men's and women's sizing XS–XXL. All measurements in inches.`,
      inLanguage:    'en-IN',
      isPartOf:      { '@id': `${SITE_URL}/#website` },
      breadcrumb:    { '@id': `${SIZE_GUIDE_URL}#breadcrumb` },
    },
    {
      '@type': 'BreadcrumbList',
      '@id':   `${SIZE_GUIDE_URL}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Size Guide', item: SIZE_GUIDE_URL },
      ],
    },
    {
      '@type':      'FAQPage',
      '@id':        `${SIZE_GUIDE_URL}#faq`,
      mainEntity:   FAQ_ITEMS.filter(f => !f.hasLink).map(f => ({
        '@type': 'Question',
        name:    f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ],
});

/* ── Inline SVG icons — zero bundle dependency ──────────────────────────── */
function IconRuler() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M21.3 8.7l-9-9a1 1 0 0 0-1.4 0l-9 9a1 1 0 0 0 0 1.4l9 9a1 1 0 0 0 1.4 0l9-9a1 1 0 0 0 0-1.4z"/>
      <line x1="9" y1="15" x2="15" y2="9"/>
    </svg>
  );
}

function IconAlert() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="10"/>
      <line x1="12" y1="8" x2="12" y2="12"/>
      <line x1="12" y1="16" x2="12.01" y2="16"/>
    </svg>
  );
}

function IconCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

function IconArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <line x1="5" y1="12" x2="19" y2="12"/>
      <polyline points="12 5 19 12 12 19"/>
    </svg>
  );
}

/* ── Accordion item — controlled, animatable ────────────────────────────── */
function FaqItem({ item, isOpen, onToggle }) {
  const answerId   = useId();
  const questionId = useId();

  return (
    <div className={`sg-faq__item${isOpen ? ' sg-faq__item--open' : ''}`}>
      <button
        type="button"
        id={questionId}
        className="sg-faq__q"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span>{item.q}</span>
        <span className="sg-faq__chevron" aria-hidden="true">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2.5"
            strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9"/>
          </svg>
        </span>
      </button>
      <div
        id={answerId}
        role="region"
        aria-labelledby={questionId}
        className="sg-faq__body"
        hidden={!isOpen}
      >
        <div className="sg-faq__a">
          {item.hasLink ? (
            <p>
              We accept returns and exchanges for size issues and defective items within 7 days of delivery.
              See our{' '}
              <Link to="/returns" className="sg-link">Returns &amp; Exchanges</Link>
              {' '}policy for full details.
            </p>
          ) : (
            <p>{item.a}</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────── */
export default function SizeGuide() {
  const [gender,    setGender]    = useState('men');
  const [openFaqs,  setOpenFaqs]  = useState(new Set());

  // Direct lookup — useMemo adds overhead for a constant object access
  const sizes       = SIZE_DATA[gender];
  const genderLabel = gender === 'men' ? "Men's" : "Women's";

  const toggleFaq = useCallback((id) => {
    setOpenFaqs(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  const pageTitle = `T-Shirt Size Guide for Men & Women | XS–XXL | ${BRAND_NAME}`;
  const pageDesc  = `Size chart and measurement guide for ${BRAND_NAME} premium t-shirts. Chest, shoulder & length for men and women XS–XXL. All measurements in inches.`;

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <link rel="canonical" href={SIZE_GUIDE_URL} />
        <link rel="prefetch" href="/men" as="document" />
        <link rel="prefetch" href="/women" as="document" />

        <meta property="og:type" content="website" />
        <meta property="og:url" content={SIZE_GUIDE_URL} />
        <meta property="og:site_name" content={BRAND_NAME} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:alt" content="Size Guide — Men's & Women's T-Shirt Sizing" />
        <meta property="og:locale" content="en_IN" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content="Size Guide — Men's & Women's T-Shirt Sizing" />

        <script type="application/ld+json">{PAGE_SCHEMA}</script>
      </Helmet>

      <Layout showFooter showBottomTab>
        <main className="sg-page" id="main-content">

          {/* Skip link — always first focusable element */}
          <a href="#sg-main" className="sg-skip">Skip to size chart</a>

          {/* ── Full-width hero band ───────────────────────────────────── */}
          <div className="sg-hero">
            <div className="sg-hero__inner">

              {/* Breadcrumb */}
              <nav className="sg-breadcrumb" aria-label="Breadcrumb">
                <ol className="sg-breadcrumb__list">
                  <li>
                    <Link to="/" className="sg-breadcrumb__link">Home</Link>
                  </li>
                  {/* Valid HTML: non-void <li> with separator content */}
                  <li aria-hidden="true" className="sg-breadcrumb__sep">
                    <span>›</span>
                  </li>
                  <li>
                    <span aria-current="page" className="sg-breadcrumb__current">
                      Size Guide
                    </span>
                  </li>
                </ol>
              </nav>

              <h1 className="sg-hero__title">Size Guide</h1>
              <p className="sg-hero__subtitle">
                All measurements in inches. Use this chart before you buy —
                returns for size issues and defective items within 7 days.
              </p>

              {/*
                Gender tablist — both tabs point to same panel (correct).
                Tab switching triggers aria-live announcement on the table.
                Mobile: full-width flex so tabs don't clip on 320px screens.
              */}
              <div
                className="sg-tabs"
                role="tablist"
                aria-label="Select size chart by gender"
              >
                {(['men', 'women'] ).map((g) => (
                  <button
                    key={g}
                    type="button"
                    role="tab"
                    id={`sg-tab-${g}`}
                    aria-selected={gender === g}
                    aria-controls="sg-chart-panel"
                    onClick={() => setGender(g)}
                    className={`sg-tabs__btn${gender === g ? ' sg-tabs__btn--active' : ''}`}
                  >
                    {g === 'men' ? "Men's" : "Women's"} Sizes
                  </button>
                ))}
              </div>

            </div>
          </div>

          {/* ── Body layout ───────────────────────────────────────────── */}
          <div className="sg-layout" id="sg-main">

            {/* ── Sticky sidebar (desktop) ─────────────────────────────── */}
            <aside className="sg-sidebar" aria-label="How to measure">
              <div className="sg-sidebar__inner">
                <p className="sg-sidebar__label">
                  <IconRuler />
                  How to Measure
                </p>

                <ol className="sg-sidebar__steps" aria-label="Measurement guide">
                  {MEASURE_STEPS.map(({ id, title, text }, i) => (
                    <li key={id} className="sg-sidebar__step">
                      <div className="sg-sidebar__step-badge" aria-hidden="true">{i + 1}</div>
                      <div>
                        <p className="sg-sidebar__step-title">{title}</p>
                        <p className="sg-sidebar__step-text">{text}</p>
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="sg-sidebar__tip" role="note">
                  <IconAlert />
                  <span>Between sizes? <strong>Size up</strong> — our fit is relaxed.</span>
                </div>

                <div className="sg-sidebar__divider" />

                <Link to="/returns" className="sg-sidebar__link">
                  Wrong size? See returns policy →
                </Link>
              </div>
            </aside>

            {/* ── Main column ──────────────────────────────────────────── */}
            <div className="sg-main">

              {/* ── Size Chart ─────────────────────────────────────────── */}
              <section
                className="sg-section"
                id="sg-chart-panel"
                role="tabpanel"
                /*
                  aria-live="polite" announces gender switch to screen readers.
                  Previously silent — users had no feedback the table changed.
                */
                aria-live="polite"
                aria-atomic="true"
                aria-labelledby={`sg-tab-${gender}`}
              >
                <h2 id="chart-h" className="sg-section__title">
                  {genderLabel} Size Chart
                </h2>
                <p className="sg-section__note">
                  All measurements in inches.{' '}
                  {gender === 'men'
                    ? "Model is 6'0\" wearing size L."
                    : "Model is 5'6\" wearing size S."}
                </p>

                {/* Table scroll wrapper — with scroll affordance mask */}
                <div
                  className="sg-table-wrap"
                  tabIndex={0}
                  aria-label="Size chart — scroll horizontally to see all columns"
                >
                  <table
                    className="sg-table"
                    aria-label={`${genderLabel} t-shirt size chart`}
                  >
                    <caption className="sg-sr-only">
                      {genderLabel} t-shirt sizes — chest, shoulder and length measurements in inches
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Size</th>
                        <th scope="col">Chest (in)</th>
                        <th scope="col">Shoulder (in)</th>
                        <th scope="col">Length (in)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sizes.map((row) => (
                        <tr key={row.size}>
                          <td><strong>{row.size}</strong></td>
                          <td>{row.chest}"</td>
                          <td>{row.shoulder}"</td>
                          <td>{row.length}"</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Tip callout */}
                <div className="sg-tip" role="note">
                  <IconAlert />
                  <span>Between sizes? Go one up — our fabrics are pre-shrunk but the fit is relaxed.</span>
                </div>

                {/*
                  Inline CTA — placed directly below the chart to capture
                  peak intent (user just found their size).
                  Previously this only appeared in the CTA band at page bottom.
                */}
                <div className="sg-chart-cta">
                  <Link
                    to={gender === 'men' ? '/men' : '/women'}
                    className="sg-btn sg-btn--gold-sm"
                    aria-label={`Shop ${genderLabel} t-shirts`}
                  >
                    Shop {genderLabel} T-Shirts
                    <IconArrowRight />
                  </Link>
                </div>
              </section>

              {/* ── How to Measure (mobile only) ──────────────────────── */}
              <section
                className="sg-section sg-measure-mobile"
                aria-labelledby="measure-h"
              >
                <h2 id="measure-h" className="sg-section__title">How to Measure</h2>
                <ol className="sg-steps" aria-label="Measurement steps">
                  {MEASURE_STEPS.map(({ id, title, text }, i) => (
                    <li key={id} className="sg-step">
                      <div className="sg-step__badge" aria-hidden="true">{i + 1}</div>
                      <div className="sg-step__body">
                        <h3 className="sg-step__title">{title}</h3>
                        <p  className="sg-step__text">{text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              {/* ── Fit Guide ──────────────────────────────────────────── */}
              <section className="sg-section" aria-labelledby="fit-h">
                <h2 id="fit-h" className="sg-section__title">Fit Guide</h2>
                <div className="sg-fit-grid">
                  {FIT_CARDS.map(({ id, title, text }) => (
                    <div key={id} className="sg-fit-card">
                      <div className="sg-fit-card__icon" aria-hidden="true">
                        <IconCheck />
                      </div>
                      <h3 className="sg-fit-card__title">{title}</h3>
                      <p  className="sg-fit-card__text">{text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* ── FAQ — controlled accordion, not <details> ──────────── */}
              <section className="sg-section" aria-labelledby="faq-h">
                <h2 id="faq-h" className="sg-section__title">Common Questions</h2>
                <div className="sg-faq">
                  {FAQ_ITEMS.map((item) => (
                    <FaqItem
                      key={item.id}
                      item={item}
                      isOpen={openFaqs.has(item.id)}
                      onToggle={() => toggleFaq(item.id)}
                    />
                  ))}
                </div>
              </section>

            </div>{/* /sg-main */}
          </div>{/* /sg-layout */}

          {/* ── Full-width CTA band ────────────────────────────────────── */}
          <div className="sg-cta-band">
            <div className="sg-cta-band__inner">
              <div className="sg-cta-band__text">
                <p className="sg-cta-band__eyebrow">You've got your size</p>
                <h2 className="sg-cta-band__title">Ready to Shop?</h2>
                <p className="sg-cta-band__sub">Free shipping on all orders</p>
              </div>
              <div className="sg-cta-band__actions">
                <Link to="/shop" className="sg-btn sg-btn--primary">
                  Shop
                  <IconArrowRight />
                </Link>
                <Link to="/men" className="sg-btn sg-btn--ghost">
                  Shop Men
                  <IconArrowRight />
                </Link>
                <Link to="/women" className="sg-btn sg-btn--ghost">
                  Shop Women
                  <IconArrowRight />
                </Link>
              </div>
            </div>
          </div>

        </main>
      </Layout>
    </>
  );
}